<template>
  <!-- Label géré par ConfigurableSearchForm.vue -->
  <div class="range-field-container">
    <!-- Champs de saisie pour min/max -->
    <div v-if="getProperty('showInputs', true)" class="range-inputs">
      <el-input
          :value="minValue"
          @input="handleMinInput"
          :placeholder="$t('general.filters.min')"
          type="number"
          :min="minLimit"
          :max="maxLimit"
          :step="step"
          :size="getProperty('size', 'medium')"
          :disabled="field.isDisabled"
          class="range-input-min"
        >
          <template v-if="prefix" slot="prepend">{{ prefix }}</template>
          <template v-if="suffix" slot="append">{{ suffix }}</template>
        </el-input>

        <span class="range-separator">-</span>

        <el-input
          :value="maxValue"
          @input="handleMaxInput"
          :placeholder="$t('general.filters.max')"
          type="number"
          :min="minLimit"
          :max="maxLimit"
          :step="step"
          :size="getProperty('size', 'medium')"
          :disabled="field.isDisabled"
          class="range-input-max"
        >
          <template v-if="prefix" slot="prepend">{{ prefix }}</template>
          <template v-if="suffix" slot="append">{{ suffix }}</template>
        </el-input>
      </div>

      <!-- Slider désactivé suite au feedback recettage -->
      <!-- Le slider était contre-intuitif, on garde seulement les champs de saisie -->

      <!-- Valeur formatée affichée -->
      <div v-if="getProperty('showValue', false)" class="range-value-display">
        <small class="text-muted">
          {{ formatRangeDisplay() }}
        </small>
      </div>

    <!-- Aide contextuelle -->
    <div v-if="field.helpText" class="field-help-text">
      <small class="text-muted">{{ $t(field.helpText) }}</small>
    </div>
  </div>
</template>

<script>
//  SLIDER GLOBAL - El-slider est déjà enregistré globalement dans Element UI

export default {
  name: 'GenericFieldRange',
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: [Object, Array],
      default: () => null
    },
    isPopupMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    fieldClass() {
      return {
        'search-field': true,
        'search-field-range': true,
        'search-field-required': this.field.isRequired,
        'search-field-disabled': this.field.isDisabled
      }
    },

    minValue() {
      if (!this.value) return ''

      if (Array.isArray(this.value)) {
        const val = this.value[0]
        return val !== null && val !== undefined && !isNaN(Number(val)) ? String(val) : ''
      }

      const fieldName = this.field.name
      const eventMapping = this.field.eventMapping
      const minKey = (eventMapping && eventMapping.minField) ? eventMapping.minField : `${fieldName}Gt`
      const val = this.value[minKey] !== undefined ? this.value[minKey] : this.value.min
      return val !== null && val !== undefined && !isNaN(Number(val)) ? String(val) : ''
    },

    maxValue() {
      if (!this.value) return ''

      if (Array.isArray(this.value)) {
        const val = this.value[1]
        return val !== null && val !== undefined && !isNaN(Number(val)) ? String(val) : ''
      }

      const fieldName = this.field.name
      const eventMapping = this.field.eventMapping
      const maxKey = (eventMapping && eventMapping.maxField) ? eventMapping.maxField : `${fieldName}Lt`
      const val = this.value[maxKey] !== undefined ? this.value[maxKey] : this.value.max
      return val !== null && val !== undefined && !isNaN(Number(val)) ? String(val) : ''
    },

    minLimit() {
      //  CONVERSION EXPLICITE EN NOMBRE pour éviter les erreurs de type
      return Number(this.getProperty('min', 0)) || 0
    },

    maxLimit() {
      //  CONVERSION EXPLICITE EN NOMBRE pour éviter les erreurs de type
      return Number(this.getProperty('max', 1000)) || 1000
    },

    step() {
      //  CONVERSION EXPLICITE EN NOMBRE pour éviter les erreurs de type
      return Number(this.getProperty('step', 1)) || 1
    },

    prefix() {
      return this.getProperty('prefix', '')
    },

    suffix() {
      return this.getProperty('suffix', '')
    },

    // sliderValue supprimé - slider désactivé suite au feedback recettage
  },
  methods: {
    handleMinInput(inputValue) {
      const numValue = inputValue ? Number(inputValue) : null
      this.updateValue(numValue, this.maxValue)
    },

    handleMaxInput(inputValue) {
      const numValue = inputValue ? Number(inputValue) : null
      this.updateValue(this.minValue, numValue)
    },

    // handleSliderChange supprimé - slider désactivé suite au feedback recettage

    updateValue(minVal, maxVal) {
      let emittedValue
      const fieldName = this.field.name

      // Utiliser eventMapping.minField / maxField
      const eventMapping = this.field.eventMapping
      if (eventMapping && eventMapping.minField && eventMapping.maxField) {
        // Utiliser le mapping défini dans le backend (ex: surfaceGt, surfaceLt)
        // Ne pas inclure les valeurs null pour éviter les problèmes avec l'API
        emittedValue = {}
        if (minVal !== null && minVal !== undefined && minVal !== '') {
          emittedValue[eventMapping.minField] = minVal
        }
        if (maxVal !== null && maxVal !== undefined && maxVal !== '') {
          emittedValue[eventMapping.maxField] = maxVal
        }
      } else {
        // Fallback : Format générique basé sur le nom du champ
        emittedValue = {}
        if (minVal !== null && minVal !== undefined && minVal !== '') {
          emittedValue[`${fieldName}Gt`] = minVal
        }
        if (maxVal !== null && maxVal !== undefined && maxVal !== '') {
          emittedValue[`${fieldName}Lt`] = maxVal
        }
      }

      this.$emit('input', emittedValue)
      this.$emit('change', {
        field: this.field.name,
        value: emittedValue,
        min: minVal,
        max: maxVal
      })
    },

    // formatTooltip supprimé - slider désactivé suite au feedback recettage

    formatValue(value) {
      if (value === null || value === undefined) return ''

      //  CONVERSION SÉCURISÉE en nombre avant formatage
      const numericValue = Number(value) || 0
      const formatType = this.getProperty('format', 'number')

      switch (formatType) {
        case 'currency':
          return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
          }).format(numericValue)
        case 'compact':
          return new Intl.NumberFormat('fr-FR', {
            notation: 'compact',
            compactDisplay: 'short'
          }).format(numericValue)
        case 'number':
        default:
          return new Intl.NumberFormat('fr-FR').format(numericValue)
      }
    },

    formatRangeDisplay() {
      if (!this.minValue && !this.maxValue) {
        return this.$t('components.searchField.noLimitSelected')
      }

      if (this.minValue && this.maxValue) {
        return `${this.formatValue(this.minValue)} - ${this.formatValue(this.maxValue)}${this.suffix}`
      } else if (this.minValue) {
        return `${this.$t('components.searchField.from')} ${this.formatValue(this.minValue)}${this.suffix}`
      } else if (this.maxValue) {
        return `${this.$t('components.searchField.upTo')} ${this.formatValue(this.maxValue)}${this.suffix}`
      }

      return ''
    },

    getProperty(propertyName, defaultValue = undefined) {
      if (!this.field.properties) {
        return defaultValue
      }

      // Nouveau format : properties est un objet simple
      if (typeof this.field.properties === 'object' && !Array.isArray(this.field.properties)) {
        return this.field.properties[propertyName] !== undefined ? this.field.properties[propertyName] : defaultValue
      }

      // Ancien format : properties est un array d'objets
      if (Array.isArray(this.field.properties)) {
        const property = this.field.properties.find(p => p.name === propertyName)
        if (!property) {
          return defaultValue
        }

        switch (property.type) {
          case 'boolean':
            return property.value === 'true' || property.value === true
          case 'number':
          case 'integer':
            return property.value ? Number(property.value) : defaultValue
          case 'string':
          default:
            return property.value || defaultValue
        }
      }

      return defaultValue
    },

    // Méthode pour fermer la popup (appelée par le bouton Valider)
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.search-field {
  margin-bottom: 16px;
}

.search-field-required .el-form-item__label::after {
  content: ' *';
  color: #f56c6c;
}

.search-field-disabled {
  opacity: 0.6;
}

.field-help-text {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
}

.text-muted {
  color: #909399;
}

/* Container principal */
.range-field-container {
  width: 100%;
}

/* Styles pour les inputs */
.range-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.range-input-min,
.range-input-max {
  flex: 1;
  min-width: 120px;
}

.range-separator {
  font-weight: bold;
  color: #909399;
  font-size: 14px;
  padding: 0 4px;
  align-self: center;
}

/* Styles pour le slider */
.range-slider {
  margin: 8px 0;
  padding: 0 12px;
}

.range-slider-component {
  width: 100%;
}

/* Styles pour l'affichage de valeur */
.range-value-display {
  text-align: center;
  margin-top: 8px;
  padding: 4px 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-size: 12px;
}

/* Styles pour les inputs avec préfixe/suffixe */
.range-inputs .el-input-group__prepend,
.range-inputs .el-input-group__append {
  background-color: #f5f7fa;
  border-color: #dcdfe6;
  color: #909399;
  font-size: 12px;
  padding: 0 8px;
  min-width: auto;
}

/* Styles responsifs */
@media (max-width: 768px) {
  .range-inputs {
    flex-direction: column;
    gap: 12px;
  }

  .range-separator {
    display: none;
  }

  .range-input-min,
  .range-input-max {
    width: 100%;
    min-width: auto;
  }

  .range-slider {
    padding: 0 8px;
  }
}

/* Animation pour les changements de valeur */
.range-field-container .el-input .el-input__inner {
  transition: border-color 0.3s ease;
}

.range-field-container .el-input .el-input__inner:focus {
  border-color: #409eff;
}

/* Styles spéciaux pour le slider ubi-slider */
.range-field-container .ubi-slider {
  margin: 16px 0;
}
</style>
